import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  ClientLink,
  Currency,
  DateTime,
  ExportDropdown,
  IconButton,
  InternalClientTooltip,
  MultiCurrency,
  Percent,
  PeriodFilter,
  ProjectLink,
  Tooltip,
} from '~/components';
import { CurrencyFilter } from '~/components/filters';
import ClientFiltersBar from '~/components/filters/ClientFiltersBar';
import ClientFiltersGroup from '~/components/filters/ClientFiltersGroup';
import FilterButton from '~/components/filters/FilterButton';
import ProjectFiltersBar from '~/components/filters/ProjectFiltersBar';
import ProjectFiltersGroup from '~/components/filters/ProjectFiltersGroup';
import SingleSelectFilter from '~/components/filters/SingleSelectFilter';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useDocumentTitle, useFeatures, useSearchParams, useSearchParamsConfig } from '~/hooks';
import useClientFilters from '~/hooks/useClientFilters';
import useProjectFilters from '~/hooks/useProjectFilters';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { dateFormats, intervalOptions, mimeTypes } from '~/utils';
import ExportDialogAsync from '../components/ExportDialogAsync';
import Report from '../components/Report';
import Table from '../components/table';
import ReportActions from '../ReportActions';

const ProjectInfo = styled.div`
  line-height: 1;

  small {
    font-size: 0.75rem;
    color: ${colors.grey40};
    display: flex;
    padding-top: 0.25rem;
  }
`;

const intervals = {
  get custom() {
    return intervalOptions.custom;
  },

  get past_30_days() {
    return {
      key: 'past_30_days',
      label: 'Past 30 days',
      start: moment().subtract(30, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_60_days() {
    return {
      key: 'past_60_days',
      label: 'Past 60 days',
      start: moment().subtract(60, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_90_days() {
    return {
      key: 'past_90_days',
      label: 'Past 90 days',
      start: moment().subtract(90, 'days').format(dateFormats.isoDate),
      end: moment().subtract(1, 'day').format(dateFormats.isoDate),
      unit: 'custom',
    };
  },

  get past_4_weeks() {
    return {
      key: 'past_4_weeks',
      label: 'Past 4 weeks',
      start: moment().subtract(4, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'week').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_12_weeks() {
    return {
      key: 'past_12_weeks',
      label: 'Past 12 weeks',
      start: moment().subtract(12, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_26_weeks() {
    return {
      key: 'past_26_weeks',
      label: 'Past 26 weeks',
      start: moment().subtract(26, 'weeks').startOf('isoWeek').format(dateFormats.isoDate),
      end: moment().subtract(1, 'weeks').endOf('isoWeek').format(dateFormats.isoDate),
      unit: 'week',
    };
  },

  get past_3_months() {
    return {
      key: 'past_3_months',
      label: 'Past 3 months',
      start: moment().subtract(3, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_6_months() {
    return {
      key: 'past_6_months',
      label: 'Past 6 months',
      start: moment().subtract(6, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_9_months() {
    return {
      key: 'past_9_months',
      label: 'Past 9 months',
      start: moment().subtract(9, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },

  get past_12_months() {
    return {
      key: 'past_12_months',
      label: 'Past 12 months',
      start: moment().subtract(12, 'months').startOf('month').format(dateFormats.isoDate),
      end: moment().subtract(1, 'month').endOf('month').format(dateFormats.isoDate),
      unit: 'month',
    };
  },
};

const intervalsByScope = {
  get day() {
    return [intervals.custom, intervals.past_30_days, intervals.past_60_days, intervals.past_90_days];
  },

  get week() {
    return [intervals.custom, intervals.past_4_weeks, intervals.past_12_weeks, intervals.past_26_weeks];
  },

  get month() {
    return [
      intervals.custom,
      intervals.past_3_months,
      intervals.past_6_months,
      intervals.past_9_months,
      intervals.past_12_months,
    ];
  },
};

export default function ServicesRevenueVarianceByProject({ report }) {
  useDocumentTitle(report.name);
  const { workspace } = useWorkspace();
  const api = useApi();

  const [query, setQuery] = useState({ report: null, status: 'loading' });

  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();

  const [params, setParams] = useState({
    period: null,
    unit: '',
    currency: workspace.currency,

    ...clientFilters.filters,
    ...projectFilters.filters,
  });

  const searchParamsConfig = useSearchParamsConfig();
  const [searchParamsStatus, setSearchParamsStatus] = useState('pending');
  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: {
          ...searchParamsConfig.periodByUnit,
          default: intervals.past_6_months,
          intervals: (unit) => intervalsByScope[unit],
          defaults: {
            day: intervals.past_30_days,
            week: intervals.past_12_weeks,
            month: intervals.past_6_months,
          },
        },
        unit: { default: 'month', valid: ['day', 'week', 'month'] },
        currency: searchParamsConfig.currency,

        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
      }),
      [searchParamsConfig, clientFilters, projectFilters],
    ),
    onChange: (params) => setParams((state) => ({ ...state, ...params })),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      unit: params.unit,
      currency: params.currency ?? undefined,

      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
    }),
    [params, clientFilters, projectFilters],
  );

  useEffect(() => {
    if (searchParamsStatus === 'ready') return;
    searchParams.get().then((params) => {
      setParams((state) => ({ ...state, ...params }));
      setSearchParamsStatus('ready');
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).reports(report.id).data(urlSearchParams);
    setQuery((state) => ({ ...state, data, status: 'ready' }));
  }, [api, workspace.id, report.id, urlSearchParams]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);
  const handleApplyFilters = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideFilters();
  };

  const [settingsVisible, setSettingsVisible] = useState(false);
  const showSettings = () => setSettingsVisible(true);
  const hideSettings = () => setSettingsVisible(false);
  const handleApplySettings = (values) => {
    if (values !== params) setQuery((state) => ({ ...state, status: 'filtering' }));

    setParams({ ...params, ...values });
    searchParams.set(values);
    hideSettings();
  };

  const confirmation = useConfirmation();

  const handleExport = (mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialogAsync
        onLoad={api.www
          .workspaces(workspace.id)
          .reports(report.id)
          .data(urlSearchParams, {
            headers: { accept: mimeType },
          })}
        onClose={resolve}
      />
    ));
  };

  const features = useFeatures();

  return (
    <Report>
      <Report.Header>
        <Report.Info report={report} />

        <Report.Actions>
          <ReportActions onSave={() => searchParams.set(params)} />

          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport(mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="cog" onClick={showSettings} />

          <FilterButton isOutline onClick={showFilters} />
        </Report.Actions>
      </Report.Header>

      <Report.FiltersBar>
        <PeriodFilter
          clearable={false}
          scope={params.unit}
          intervals={intervalsByScope[params.unit]}
          placeholder="Date Range"
          value={params.period}
          onChange={({ target: { value } }) => handleApplyFilters({ period: value })}
        />

        <UnitFilter
          value={params.unit}
          onChange={({ target: { value } }) =>
            handleApplyFilters({
              unit: value,
              period: {
                day: intervals.past_30_days,
                week: intervals.past_12_weeks,
                month: intervals.past_6_months,
              }[value],
            })
          }
        />

        {features.multicurrency && (
          <CurrencyFilter
            value={params.currency}
            onChange={({ target: { value } }) => handleApplySettings({ currency: value })}
          />
        )}

        <ClientFiltersBar filters={params} onChange={handleApplyFilters} />

        <ProjectFiltersBar filters={params} onChange={handleApplyFilters} />
      </Report.FiltersBar>

      {(() => {
        switch (query.status) {
          case 'loading':
          case 'filtering':
            return <PageLoader />;

          default:
            return (
              <>
                <Data query={query} params={params} />
                <Settings
                  values={params}
                  isOpen={settingsVisible}
                  onApply={handleApplySettings}
                  onClose={hideSettings}
                />
                <Filters values={params} isOpen={filtersVisible} onApply={handleApplyFilters} onClose={hideFilters} />
              </>
            );
        }
      })()}
    </Report>
  );
}

function Data({ query, params }) {
  const report = query.data;

  const { projects, records, periods } = useMemo(() => {
    const projects = _.orderBy(report.projects, (p) => p.name.toLowerCase());

    const records = report.records.reduce(
      (a, v) => {
        a.total.convertedActual += v.convertedActual || 0;
        a.total.convertedPlanned += v.convertedPlanned || 0;

        // Projects
        a.projects[v.projectId] = a.projects[v.projectId] || {
          total: {
            currency: v.currency,
            convertedCurrency: v.convertedCurrency,
            actual: 0,
            planned: 0,
            get variance() {
              return this.actual - this.planned;
            },
            get variancePercentage() {
              if (!this.planned) return null;
              return this.variance / this.planned;
            },

            convertedActual: 0,
            convertedPlanned: 0,
            get convertedVariance() {
              return this.convertedActual - this.convertedPlanned;
            },
          },
          periods: {},
        };
        a.projects[v.projectId].total.actual += v.actual || 0;
        a.projects[v.projectId].total.planned += v.planned || 0;
        a.projects[v.projectId].total.convertedActual += v.convertedActual || 0;
        a.projects[v.projectId].total.convertedPlanned += v.convertedPlanned || 0;

        a.projects[v.projectId].periods[v.start] = v;

        // Periods
        if (!a.periods[v.start])
          a.periods[v.start] = {
            convertedActual: 0,
            convertedPlanned: 0,
            get convertedVariance() {
              return this.convertedActual - this.convertedPlanned;
            },
            get variancePercentage() {
              if (!this.convertedPlanned) return null;
              return this.convertedVariance / this.convertedPlanned;
            },
          };
        a.periods[v.start].convertedActual += v.convertedActual || 0;
        a.periods[v.start].convertedPlanned += v.convertedPlanned || 0;

        return a;
      },
      {
        projects: {},
        periods: {},
        total: {
          convertedActual: 0,
          convertedPlanned: 0,
          get convertedVariance() {
            return this.convertedActual - this.convertedPlanned;
          },
          get variancePercentage() {
            if (!this.convertedPlanned) return null;
            return this.convertedVariance / this.convertedPlanned;
          },
        },
      },
    );

    let start = params.period.start;
    let end = params.period.end;

    switch (params.unit) {
      case 'day':
        start = moment(start).format(dateFormats.isoDate);
        end = moment(end).format(dateFormats.isoDate);
        break;

      case 'week':
        start = moment(start).startOf('isoWeek').format(dateFormats.isoDate);
        end = moment(end).endOf('isoWeek').format(dateFormats.isoDate);
        break;

      case 'month':
        start = moment(start).startOf('month').format(dateFormats.isoDate);
        end = moment(end).endOf('month').format(dateFormats.isoDate);
        break;
    }

    const periodCount = moment(end).diff(start, params.unit) + 1;
    const periods = [];
    for (let index = 0; index < periodCount; index++) {
      periods.push(moment(start).add(index, params.unit).format(dateFormats.isoDate));
    }

    return { projects, records, periods };
  }, [report, params.period.start, params.period.end, params.unit]);

  return (
    <Report.Table>
      <Table>
        <Table.Header>
          <Table.Column sticky minWidth="16rem">
            Project/Client
          </Table.Column>

          {periods.map((p) => (
            <Table.Column key={p} align="right" width="12rem">
              <DateTime value={p} />
            </Table.Column>
          ))}

          <Table.Column align="right" width="12rem">
            Total
          </Table.Column>
        </Table.Header>

        <Table.Body>
          {projects.map((project) => {
            return (
              <Table.Row key={project.id}>
                <Table.Cell>
                  <ProjectInfo>
                    <p>
                      <ProjectLink project={project} />
                    </p>
                    <small>
                      <ClientLink client={project.client} />
                      {project.client.isInternal && <InternalClientTooltip />}
                    </small>
                  </ProjectInfo>
                </Table.Cell>

                {periods.map((period) => {
                  const value = records.projects[project.id]?.periods[period];

                  return (
                    <Table.Cell key={period}>
                      <CellValue value={value} />
                    </Table.Cell>
                  );
                })}

                <Table.Cell>
                  <CellValue value={records.projects[project.id]?.total} />
                </Table.Cell>
              </Table.Row>
            );
          })}

          <Table.Row style={{ fontWeight: weights.bold }}>
            <Table.Cell>Total</Table.Cell>

            {periods.map((period) => {
              const value = records.periods[period];

              return (
                <Table.Cell key={period}>
                  <CellValue value={value} />
                </Table.Cell>
              );
            })}

            <Table.Cell>
              <CellValue value={records.total} />
            </Table.Cell>
          </Table.Row>
        </Table.Body>

        <Table.Status
          total={projects.length}
          label="Project"
          isLoading={query.status !== 'ready'}
          partial={report.partialResults}
        />
      </Table>
    </Report.Table>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <Report.FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <UnitFilter
        value={filters.unit}
        onChange={({ target: { value } }) =>
          handleFilter({
            unit: value,
            period: {
              day: intervals.past_30_days,
              week: intervals.past_12_weeks,
              month: intervals.past_6_months,
            }[value],
          })
        }
      />

      <PeriodFilter
        clearable={false}
        scope={filters.unit}
        intervals={intervalsByScope[filters.unit]}
        placeholder="Date Range"
        value={filters.period}
        onChange={({ target: { value } }) => handleFilter({ period: value })}
      />

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />
    </Report.FiltersDrawer>
  );
}

function Settings({ values, isOpen, onClose, onApply }) {
  const features = useFeatures();
  const [settings, setSettings] = useState(values);

  const handleApply = () => {
    onApply(settings);
  };

  const handleChange = (filter) => {
    setSettings({ ...settings, ...filter });
  };

  const handleCancel = () => {
    setSettings(values);
    onClose();
  };

  useEffect(() => {
    setSettings(values);
  }, [values]);

  return (
    <Report.FiltersDrawer title="Settings" isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      {features.multicurrency && (
        <CurrencyFilter
          value={settings.currency}
          onChange={({ target: { value } }) => handleChange({ currency: value })}
        />
      )}
    </Report.FiltersDrawer>
  );
}

const TooltipContainer = styled.div`
  min-width: 14rem;
  padding: 0.75rem 0;
`;

const TooltipInfo = styled.div`
  display: flex;
  font-size: 0.75rem;

  &:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const TooltipLabel = styled.div`
  width: 7rem;
  padding-right: 0.5rem;
  color: ${colors.grey40};
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  flex-shrink: 0;
  white-space: wrap;
`;

const TooltipValue = styled.div`
  white-space: nowrap;
`;

function CellValue({ value }) {
  if (!value) return <Currency value={null} />;

  return (
    <Tooltip
      message={
        <TooltipContainer>
          <TooltipInfo>
            <TooltipLabel>Actual</TooltipLabel>
            <TooltipValue>
              <MultiCurrency
                value={[
                  { value: value.convertedActual, currency: value.convertedCurrency },
                  { value: value.actual, currency: value.currency },
                ]}
              />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Planned</TooltipLabel>
            <TooltipValue>
              <MultiCurrency
                value={[
                  { value: value.convertedPlanned, currency: value.convertedCurrency },
                  { value: value.planned, currency: value.currency },
                ]}
              />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Variance</TooltipLabel>
            <TooltipValue style={{ color: value.variance < 0 ? colors.danger : colors.black }}>
              <MultiCurrency
                value={[
                  { value: value.convertedVariance, currency: value.convertedCurrency },
                  { value: value.variance, currency: value.currency },
                ]}
              />
            </TooltipValue>
          </TooltipInfo>

          <TooltipInfo>
            <TooltipLabel>Variance %</TooltipLabel>
            <TooltipValue style={{ color: value.variancePercentage < 0 ? colors.danger : colors.black }}>
              <Percent value={value.variancePercentage} />
            </TooltipValue>
          </TooltipInfo>
        </TooltipContainer>
      }>
      <span style={{ color: value.convertedVariance < 0 ? colors.danger : colors.black }}>
        <MultiCurrency
          value={[
            { value: value.convertedVariance, currency: value.convertedCurrency },
            { value: value.variance, currency: value.currency },
          ]}
        />
      </span>
    </Tooltip>
  );
}

function UnitFilter(props) {
  return (
    <SingleSelectFilter
      icon="gear"
      placeholder="Unit"
      clearable={false}
      renderValue={(value) => value.name}
      options={[
        { id: 'month', name: 'Month' },
        { id: 'week', name: 'Week' },
        { id: 'day', name: 'Day' },
      ]}
      {...props}
    />
  );
}
